import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Check, ArrowRight, Star, Zap, Shield, Users, MessageCircle, CreditCard, Building2, TrendingUp } from 'lucide-react';

const PricingPage = () => {
  const plans = [
    {
      name: 'Essential',
      price: 39.99,
      description: 'Perfect for small businesses getting started',
      features: [
        'Send invoices',
        'Receive invoices',
        'Automatic payment collection between users',
        'Single user access',
        'Customer incentive for early payment',
        'Guest invoice portal',
        'Real-time dashboard',
        'Automatic approvals (7-day rule)',
        'Scheduled payments',
        'Invoice tracking dashboard',
        'Secure payment',
        'Contact management',
        'Smart to-do lists',
        'Global search',
        'Stripe Connect integration',
        'QuickBooks & Xero integration'
      ],
      cta: 'Choose Essential',
      popular: false,
      color: 'from-blue-500 to-blue-600'
    },
    {
      name: 'Pro',
      price: 49.99,
      description: 'For growing businesses that need more power',
      features: [
        'Everything in Essential',
        'Priority support',
        'Add up to 4 additional users',
        'Chat with customers',
        'Advanced analytics & insights',
        'Enhanced payment metrics',
        'Team collaboration tools',
        'User management & permissions'
      ],
      cta: 'Choose Pro',
      popular: true,
      color: 'from-purple-500 to-purple-600'
    }
  ];

  const comparisonFeatures = [
    { name: 'Send invoices', essential: true, pro: true },
    { name: 'Receive invoices', essential: true, pro: true },
    { name: 'Automatic payment collection between users', essential: true, pro: true },
    { name: 'Single user access', essential: true, pro: true },
    { name: 'Customer incentive for early payment', essential: true, pro: true },
    { name: 'Guest invoice portal', essential: true, pro: true },
    { name: 'Real-time dashboard', essential: true, pro: true },
    { name: 'Automatic approvals (7-day rule)', essential: true, pro: true },
    { name: 'Scheduled payments', essential: true, pro: true },
    { name: 'Invoice tracking dashboard', essential: true, pro: true },
    { name: 'Secure payment', essential: true, pro: true },
    { name: 'Contact management', essential: true, pro: true },
    { name: 'Smart to-do lists', essential: true, pro: true },
    { name: 'Global search', essential: true, pro: true },
    { name: 'Stripe Connect integration', essential: true, pro: true },
    { name: 'QuickBooks & Xero integration', essential: true, pro: true },
    { name: 'Priority support', essential: false, pro: true },
    { name: 'Add up to 4 additional users', essential: false, pro: true },
    { name: 'Chat with customers', essential: false, pro: true },
    { name: 'Advanced analytics & insights', essential: true, pro: true },
    { name: 'Enhanced payment metrics', essential: true, pro: true },
    { name: 'Team collaboration tools', essential: true, pro: true },
    { name: 'User management & permissions', essential: true, pro: true }
  ];

  return (
    <>
      <Helmet>
        <title>Pricing - From £39.99/month | Receevit® Credit Control Software</title>
        <meta name="description" content="Simple, transparent pricing for B2B credit control automation. Essential £39.99/month, Pro £49.99/month. Free 30-day trial. Cancel anytime." />
        <link rel="canonical" href="https://www.receevit.com/pricing" />
        <meta property="og:title" content="Pricing - From £39.99/month | Receevit® Credit Control Software" />
        <meta property="og:description" content="Simple, transparent pricing for B2B credit control automation. Essential £39.99/month, Pro £49.99/month. Free 30-day trial. Cancel anytime." />
        <meta property="og:url" content="https://www.receevit.com/pricing" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.receevit.com/og-image.png" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Pricing - From £39.99/month | Receevit® Credit Control Software",
            "url": "https://www.receevit.com/pricing",
            "description": "Simple, transparent pricing for B2B credit control automation. Essential £39.99/month, Pro £49.99/month. Free 30-day trial. Cancel anytime.",
            "publisher": {
              "@type": "Organization",
              "name": "Receevit®",
              "url": "https://www.receevit.com/"
            }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://www.receevit.com/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Pricing",
                "item": "https://www.receevit.com/pricing"
              }
            ]
          })}
        </script>
      </Helmet>
      
      <div className="pt-16 bg-[#1F11ED] min-h-screen">
        {/* Simple Header */}
        <section className="py-12 bg-[#1F11ED]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Choose Your Plan
          </h1>
          <p className="text-xl text-blue-100">
            30-day free trial • Cancel anytime
          </p>
        </div>
      </section>

      {/* Pricing Cards - Compact Side-by-Side */}
      <section className="py-16 bg-[#1F11ED]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-6">
            {plans.map((plan, index) => (
              <div
                key={plan.name}
                className={`flex-1 bg-white/10 backdrop-blur-sm rounded-xl shadow-lg border-2 ${
                  plan.popular 
                    ? 'border-white/30 ring-2 ring-white/20' 
                    : 'border-white/20'
                } p-6 hover:shadow-xl transition-all duration-300`}
              >
                {plan.popular && (
                  <div className="text-center mb-4">
                    <span className="inline-block bg-[#1F11ED] text-white px-4 py-1 rounded-full text-sm font-medium">
                      Most Popular
                    </span>
                  </div>
                )}

                {/* Header */}
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                  <div className="mb-4">
                    <span className="text-4xl font-bold text-white">£{plan.price}</span>
                    <span className="text-blue-100">/month</span>
                  </div>
                  <p className="text-blue-100 text-sm mb-6">{plan.description}</p>
                </div>

                {/* Features - Compact List */}
                <div className="mb-6">
                  {plan.features.slice(0, 4).map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center space-x-2 mb-2">
                      <Check className="w-4 h-4 text-white flex-shrink-0" aria-label="Feature included" />
                      <span className="text-blue-100 text-sm">{feature}</span>
                    </div>
                  ))}
                  {plan.features.length > 4 && (
                    <div className="text-center mt-3">
                      <span className="text-xs text-blue-200">+{plan.features.length - 4} more features</span>
                    </div>
                  )}
                </div>

                {/* CTA */}
                <Link
                  to={`/signup?plan=${plan.name.toLowerCase()}`}
                  className={`w-full bg-white text-[#1F11ED] py-3 px-4 rounded-lg font-semibold hover:bg-blue-50 transition-colors duration-200 text-center block`}
                >
                  {plan.cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Pro Section */}
      <section className="py-16 bg-[#1F11ED]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-8">
            Why Choose Pro?
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="p-6 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
              <Users className="w-12 h-12 text-white mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-3">Grow Your Business</h3>
              <p className="text-blue-100">
                Add up to 4 additional team members to manage invoices, track payments, and handle customer relationships as your business scales.
              </p>
            </div>
            
            <div className="p-6 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
              <MessageCircle className="w-12 h-12 text-white mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-3">Communicate with Customers</h3>
              <p className="text-blue-100">
                Built-in chat lets you communicate directly with customers about invoices, payment questions, and resolve issues quickly.
              </p>
                </div>
            
            <div className="p-6 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
              <Zap className="w-12 h-12 text-white mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-3">Get Priority Support</h3>
              <p className="text-blue-100">
                Fast-track assistance when you need it most. Priority support ensures your invoicing never stops.
              </p>
                </div>
              </div>
              
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-8 border border-white/20">
            <h3 className="text-2xl font-bold mb-4 text-white">Ready to Get Paid Faster?</h3>
            <p className="text-blue-100 mb-6 text-lg">
              Pro plan gives you the tools to communicate with customers, manage a team, and get the support you need to grow your business.
            </p>
              <Link
              to="/signup?plan=pro"
              className="inline-flex items-center bg-white text-[#1F11ED] px-8 py-3 rounded-lg font-semibold text-lg hover:bg-blue-50 transition-colors duration-200"
              >
              Upgrade to Pro
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </div>
        </div>
      </section>

      {/* Simple Comparison Table */}
      <section className="py-12 bg-[#1F11ED]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-white text-center mb-8">
            Feature Comparison
            </h2>
          
          <div className="bg-white/10 backdrop-blur-sm rounded-lg shadow overflow-hidden border border-white/20">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/20">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-white">Features</th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-white">Essential</th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-white">Pro</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/20">
                  {comparisonFeatures.map((feature, index) => (
                    <tr key={index}>
                      <td className="px-4 py-3 text-sm text-white">{feature.name}</td>
                      <td className="px-4 py-3 text-center">
                        {feature.essential ? (
                          <Check className="w-4 h-4 text-white mx-auto" />
                        ) : (
                          <span className="text-blue-200">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {feature.pro ? (
                          <Check className="w-4 h-4 text-white mx-auto" />
                        ) : (
                          <span className="text-blue-200">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Simple Trust Section */}
      <section className="py-12 bg-[#1F11ED]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-8">
            Trust & Security
            </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-6 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
              <CreditCard className="w-8 h-8 text-white mx-auto mb-3" />
              <h3 className="font-semibold text-white mb-2">Secure payments powered by Stripe</h3>
              <p className="text-blue-100 text-sm">Bank-level security for all transactions</p>
            </div>
            
            <div className="p-6 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
              <Building2 className="w-8 h-8 text-white mx-auto mb-3" />
              <h3 className="font-semibold text-white mb-2">Built for modern B2B businesses</h3>
              <p className="text-blue-100 text-sm">Designed with enterprise needs in mind</p>
            </div>
            
            <div className="p-6 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
              <TrendingUp className="w-8 h-8 text-white mx-auto mb-3" />
              <h3 className="font-semibold text-white mb-2">Get paid faster, reduce errors, improve cash flow</h3>
              <p className="text-blue-100 text-sm">Streamline your invoicing process</p>
            </div>
          </div>
        </div>
      </section>
      </div>
    </>
  );
};

export default PricingPage;
